import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { supabase } from '../../../lib/supabase';


const VARegistrationForm = ({ onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    militaryBranch: '',
    serviceYears: '',
    clearanceLevel: '',
    specializations: [],
    agreeToTerms: false,
    agreeToPrivacy: false
  });

  const [formErrors, setFormErrors] = useState({});

  const militaryBranches = [
    { value: 'army', label: 'U.S. Army' },
    { value: 'navy', label: 'U.S. Navy' },
    { value: 'air_force', label: 'U.S. Air Force' },
    { value: 'marines', label: 'U.S. Marine Corps' },
    { value: 'coast_guard', label: 'U.S. Coast Guard' },
    { value: 'space_force', label: 'U.S. Space Force' }
  ];

  const clearanceLevels = [
    { value: 'none', label: 'No Clearance' },
    { value: 'confidential', label: 'Confidential' },
    { value: 'secret', label: 'Secret' },
    { value: 'top_secret', label: 'Top Secret' },
    { value: 'ts_sci', label: 'TS/SCI' }
  ];

  const specializationOptions = [
    { value: 'logistics', label: 'Logistics & Supply Chain' },
    { value: 'security', label: 'Security Operations' },
    { value: 'administration', label: 'Administration' },
    { value: 'it', label: 'Information Technology' },
    { value: 'medical', label: 'Medical Services' },
    { value: 'communications', label: 'Communications' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (formErrors?.[field]) {
      setFormErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData?.fullName?.trim()) errors.fullName = 'Full name is required';
    if (!formData?.email?.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      errors.email = 'Invalid email format';
    }
    if (!formData?.phone?.trim()) errors.phone = 'Phone number is required';
    if (!formData?.password) {
      errors.password = 'Password is required';
    } else if (formData?.password?.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }
    if (formData?.password !== formData?.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    if (!formData?.militaryBranch) errors.militaryBranch = 'Military branch is required';
    if (!formData?.serviceYears) errors.serviceYears = 'Years of service is required';
    if (!formData?.clearanceLevel) errors.clearanceLevel = 'Clearance level is required';
    if (!formData?.agreeToTerms) errors.agreeToTerms = 'You must agree to the terms';
    if (!formData?.agreeToPrivacy) errors.agreeToPrivacy = 'You must agree to the privacy policy';

    setFormErrors(errors);
    return Object.keys(errors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError(null);

    if (!validateForm()) {
      setError('Please fix the errors in the form');
      return;
    }

    setLoading(true);

    try {
      // Create auth user
      const { data: authData, error: authError } = await supabase?.auth?.signUp({
        email: formData?.email,
        password: formData?.password,
        options: {
          data: {
            full_name: formData?.fullName,
            phone: formData?.phone,
            role: 'va'
          }
        }
      });

      if (authError) throw authError;

      // Create VA application
      const { error: appError } = await supabase?.from('va_applications')?.insert({
        user_id: authData?.user?.id,
        applicant_name: formData?.fullName,
        email: formData?.email,
        phone: formData?.phone,
        military_branch: formData?.militaryBranch,
        service_years: parseInt(formData?.serviceYears),
        clearance_level: formData?.clearanceLevel,
        skills: formData?.specializations,
        status: 'pending'
      });

      if (appError) throw appError;

      setSuccess(true);
      setTimeout(() => {
        onSuccess?.();
      }, 2000);
    } catch (err) {
      setError(err?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="glass-container rounded-2xl p-8 text-center space-y-4">
        <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mx-auto">
          <Icon name="CheckCircle" size={32} className="text-success" />
        </div>
        <h3 className="text-2xl font-bold text-foreground">Registration Successful!</h3>
        <p className="text-muted-foreground">
          Your VA application has been submitted. Please check your email to verify your account.
        </p>
        <p className="text-sm text-muted-foreground">
          Redirecting to dashboard...
        </p>
      </div>
    );
  }

  return (
    <div className="glass-container rounded-2xl p-8">
      <div className="space-y-6">
        {/* Header */}
        <div className="space-y-2">
          <h2 className="text-3xl font-bold text-foreground">VA Candidate Registration</h2>
          <p className="text-muted-foreground">
            Complete your registration to begin the verification process.
          </p>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="bg-error/10 border border-error/20 rounded-lg p-4 flex items-start gap-3">
            <Icon name="AlertCircle" size={20} className="text-error mt-0.5" />
            <p className="text-sm text-error">{error}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Personal Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground">Personal Information</h3>
            
            <Input
              label="Full Name"
              required
              value={formData?.fullName}
              onChange={(e) => handleChange('fullName', e?.target?.value)}
              error={formErrors?.fullName}
              placeholder="John Doe"
            />

            <div className="grid md:grid-cols-2 gap-4">
              <Input
                label="Email Address"
                type="email"
                required
                value={formData?.email}
                onChange={(e) => handleChange('email', e?.target?.value)}
                error={formErrors?.email}
                placeholder="john.doe@example.com"
              />

              <Input
                label="Phone Number"
                type="tel"
                required
                value={formData?.phone}
                onChange={(e) => handleChange('phone', e?.target?.value)}
                error={formErrors?.phone}
                placeholder="+1 (555) 123-4567"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <Input
                label="Password"
                type="password"
                required
                value={formData?.password}
                onChange={(e) => handleChange('password', e?.target?.value)}
                error={formErrors?.password}
                description="Minimum 8 characters"
              />

              <Input
                label="Confirm Password"
                type="password"
                required
                value={formData?.confirmPassword}
                onChange={(e) => handleChange('confirmPassword', e?.target?.value)}
                error={formErrors?.confirmPassword}
              />
            </div>
          </div>

          {/* Military Service Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground">Military Service Information</h3>
            
            <div className="grid md:grid-cols-2 gap-4">
              <Select
                label="Military Branch"
                required
                options={militaryBranches}
                value={formData?.militaryBranch}
                onChange={(value) => handleChange('militaryBranch', value)}
                error={formErrors?.militaryBranch}
                placeholder="Select branch"
              />

              <Input
                label="Years of Service"
                type="number"
                required
                value={formData?.serviceYears}
                onChange={(e) => handleChange('serviceYears', e?.target?.value)}
                error={formErrors?.serviceYears}
                placeholder="5"
                min="0"
              />
            </div>

            <Select
              label="Security Clearance Level"
              required
              options={clearanceLevels}
              value={formData?.clearanceLevel}
              onChange={(value) => handleChange('clearanceLevel', value)}
              error={formErrors?.clearanceLevel}
              placeholder="Select clearance level"
            />

            <Select
              label="Specializations"
              multiple
              searchable
              options={specializationOptions}
              value={formData?.specializations}
              onChange={(value) => handleChange('specializations', value)}
              description="Select all that apply"
              placeholder="Select specializations"
            />
          </div>

          {/* Terms and Conditions */}
          <div className="space-y-3">
            <Checkbox
              checked={formData?.agreeToTerms}
              onChange={(e) => handleChange('agreeToTerms', e?.target?.checked)}
              label="I agree to the Terms of Service"
              error={formErrors?.agreeToTerms}
              required
            />

            <Checkbox
              checked={formData?.agreeToPrivacy}
              onChange={(e) => handleChange('agreeToPrivacy', e?.target?.checked)}
              label="I agree to the Privacy Policy and consent to data processing"
              error={formErrors?.agreeToPrivacy}
              required
            />
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            fullWidth
            loading={loading}
            iconName="UserCheck"
          >
            Complete Registration
          </Button>
        </form>
      </div>
    </div>
  );
};

export default VARegistrationForm;